import { ReactNode } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useTranslation } from "react-i18next";
import { RestaurantType } from "src/business/restaurant/restaurant";
import { InfoCard } from "src/design";
import {
  colorBorder,
  colorTextMuted,
  colorTextPrimary,
  iconBgSuccess,
  iconColorSuccess,
  radiusChip,
} from "src/design/tokens";
import { CardLink } from "./CardLink";

interface RestaurantInfoCardProps {
  restaurant: RestaurantType;
  /** Today's service windows, already formatted. Empty when closed. */
  openingWindows: string[];
  isLoading: boolean;
}

const InfoRow = ({
  icon,
  label,
  value,
  isLast,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  isLast: boolean;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1.5,
      py: 1.25,
      borderBottom: isLast ? "none" : `1px solid ${colorBorder}`,
    }}
  >
    <Box
      sx={{
        width: 34,
        height: 34,
        flexShrink: 0,
        borderRadius: radiusChip,
        backgroundColor: iconBgSuccess,
        color: iconColorSuccess,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: "12px", color: colorTextMuted }}>
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: "14px", fontWeight: 600, color: colorTextPrimary }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

export const RestaurantInfoCard = ({
  restaurant,
  openingWindows,
  isLoading,
}: RestaurantInfoCardProps) => {
  const { t } = useTranslation();

  const location = [restaurant.quartier, restaurant.city]
    .filter(Boolean)
    .join(", ");

  const todaysHours = openingWindows.length
    ? `${t("dashboard.restaurantInfo.open")} · ${openingWindows.join(" / ")}`
    : t("dashboard.restaurantInfo.closedToday");

  return (
    <InfoCard
      title={t("dashboard.restaurantInfo.title")}
      subtitle={[restaurant.name, location].filter(Boolean).join(" · ")}
      action={
        <CardLink
          to={`/restaurant/${restaurant.id}/info`}
          label={t("dashboard.edit")}
        />
      }
      sx={{ height: "100%" }}
    >
      <InfoRow
        icon={<PlaceOutlinedIcon sx={{ fontSize: 16 }} />}
        label={t("dashboard.restaurantInfo.address")}
        value={restaurant.address || "—"}
        isLast={false}
      />
      <InfoRow
        icon={<PhoneOutlinedIcon sx={{ fontSize: 16 }} />}
        label={t("dashboard.restaurantInfo.phone")}
        value={restaurant.phone || "—"}
        isLast={false}
      />
      {isLoading ? (
        <Skeleton variant="rounded" height={48} sx={{ mt: 1.25 }} />
      ) : (
        <InfoRow
          icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
          label={t("dashboard.restaurantInfo.today")}
          value={todaysHours}
          isLast
        />
      )}
    </InfoCard>
  );
};
